import React, { useCallback, useEffect, useRef, useState } from "react";
import DefaultSelect from "./form/DefaultSelect";
import { Alert, Button, Divider, Input, Modal } from "antd";
import useLang from "../hooks/useLang";
import "./FilterSettlementsSection.css";
import { ReactComponent as PrintIcon } from "../assets/print-icon.svg";
import { ReactComponent as CodeIcon } from "../assets/codeinput.svg";
import DefaultInput from "./form/DefaultInput";
import api from "../utils/axios";
import {
	fetchSettlementStatuses,
	getConfigurationValue,
} from "../lib/OptionListApi";
import DefaultDatePicker from "./form/DefaultDatePicker";
import { ReactComponent as SearchIcon } from "../assets/search-find.svg";
import { useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { hasAccess } from "../lib/MenuValidation";

const { TextArea } = Input;

interface FilterCriteria {
	code?: string | null;
	selectedStaff?: [] | null;
	selectedStatus?: [] | null;
	orderDescription?: [] | null;
	filterDateType?: string | null;
	fromDate?: Date | null;
	toDate?: Date | null;
	selectedSeller?: [] | null;
	selectedProcessor?: [] | null;
}
interface IFilterSettlementsSection {
	showRequestButton?: boolean;
	showAdminFilters?: boolean;
	title?: string;
	buttonTitle?: string;
	onFilterChange?: any;
	selectedOptions?: any;
	onSelectedOrderStatusChange?: any;
	deliverToReturnStorage?: any;
	onRequestButtonClick?: any;
	onPrintTickets?: any;
	settlementLength?: number;
	showReturnWarehouseManager?: boolean;
	isRefusal?: boolean;
}

const FilterSettlementsSection = ({
	showRequestButton,
	title,
	buttonTitle,
	onFilterChange,
	selectedOptions,
	onSelectedOrderStatusChange,
	showAdminFilters = false,
	showReturnWarehouseManager = false,
	onRequestButtonClick,
	onPrintTickets,
	settlementLength,
	isRefusal = false,
	deliverToReturnStorage,
}: IFilterSettlementsSection) => {
	const { strings } = useLang("settlement");
	const [code, setCode] = useState();
	const [staffOptionDropDown, setStaffOptionDropDown] = useState<any[]>();
	const [processedByOptionDropDown, setProcessedByOptionDropDown] =
		useState<any[]>();
	const [settlementStatusOptions, setSettlementStatusOptions] =
		useState<any>();
	const [fromDate, setFromDate] = useState(null);
	const [toDate, setToDate] = useState(null);
	const [orderDescription, setOrderDescription] = useState(null);
	const [statusIds, setSelectedStatus] = useState(null);
	const [filterDateType, setFilterDateType] = useState("");
	const [staffIds, setSelectedStaff] = useState("");
	const [seller, setSeller] = useState([]);
	const [sellerIds, setSelectedSeller] = useState("");
	const [processedByIds, setProcessedByIds] = useState("");
	const idTimeout = useRef<any>(null);
	const customOptions = [
		{
			label: strings?.registration_date,
			value: "registration_date",
		},
		{
			label: strings?.acceptance_date,
			value: "acceptance_date",
		},
		{
			label: strings?.delivered_date,
			value: "closure_date",
		},
		{
			label: strings?.closure_date,
			value: "closure_date",
		},
	];

	const handleCodeChange = (e: any) => {
		const codeValue = e.target.value;
		setCode(codeValue);
		clearTimeout(idTimeout.current);
		idTimeout.current = setTimeout(() => {
			onFilterChange(
				codeValue,
				staffIds,
				statusIds,
				orderDescription,
				fromDate,
				toDate,
				sellerIds,
				processedByIds
			);
		}, 1000);
	};

	// Handle seller change
	const handleSellerChange = (value: any) => {
		setSelectedSeller(value);
		onFilterChange(
			code,
			staffIds,
			statusIds,
			orderDescription,
			fromDate,
			toDate,
			value,
			processedByIds
		);
	};

	// Handle status change
	const handleStatusChange = (value: any) => {
		setSelectedStatus(value);
		console.log(value, "values selected");
		onFilterChange(
			code,
			staffIds,
			value,
			orderDescription,
			fromDate,
			toDate,
			sellerIds,
			processedByIds
		);
	};

	// Handle staff change
	const handleStaffChange = (value: any) => {
		setSelectedStaff(value);

		onFilterChange(
			code,
			value,
			statusIds,
			orderDescription,
			fromDate,
			toDate,
			sellerIds,
			processedByIds
		);
	};

	// Handle order description change
	const handleOrderDescriptionChange = (e: any) => {
		const description = e.target.value;
		setOrderDescription(description);

		onFilterChange(
			code,
			staffIds,
			statusIds,
			description,
			fromDate,
			toDate,
			sellerIds,
			processedByIds
		);
	};

	// Handle date type change
	const handleDateTypeChange = (value: any) => {
		setFilterDateType(value);

		onFilterChange?.({
			code,
			selectedStaff: staffIds,
			selectedStatus: statusIds,
			orderDescription,
			filterDateType: value,
			fromDate,
			toDate,
			selectedSeller: sellerIds,
			processedByIds,
		});
	};

	// Handle from date change
	const handleFromDateChange = (date: any) => {
		setFromDate(date);

		onFilterChange(
			code,
			staffIds,
			statusIds,
			orderDescription,
			date,
			toDate,
			sellerIds,
			processedByIds
		);
	};

	// Handle to date change
	const handleToDateChange = (date: any) => {
		setToDate(date);

		onFilterChange(
			code,
			staffIds,
			statusIds,
			orderDescription,
			fromDate,
			date,
			sellerIds,
			processedByIds
		);
	};

	const handleProcessedByChange = (value: any) => {
		setProcessedByIds(value);

		onFilterChange(
			code,
			staffIds,
			statusIds,
			orderDescription,
			fromDate,
			toDate,
			sellerIds,
			value
		);
	};

	const macedonianTranslationsStatuses: any = {
		1: "Побарано",
		2: "Во процес",
		3: "Во транспорт",
		4: "Повлечено",
		5: "Потврдено",
		6: "Вратено во магацин",
		7: "Испорачано",
		8: "На чекање",
	};

	const currentLang = useSelector(
		(state: RootState) => state.langReducer.lang
	);

	const fetchUsersAndUserRoles = async () => {
		const statuses = await fetchSettlementStatuses();

		if (statuses) {
			const settlementStatuses = statuses
				.filter(
					(item: any) => isRefusal || (item.id !== 6 && item.id !== 8)
				)
				.map((item: any) => ({
					value: item.id,
					label:
						currentLang === "mk"
							? macedonianTranslationsStatuses[item.id] ||
							  item.description
							: item.description,
				}));
			setSettlementStatusOptions(settlementStatuses);
		}
		try {
			const response = await api.get("User/staff");
			if (response?.status === 200) {
				const result = response?.data;
				const staffDropDown = result
					.filter((item: any) => item.userRoleId !== 2)
					.map((item: any) => ({
						value: item.id,
						label:
							item.firstName +
							" " +
							item.lastName +
							" ( " +
							item.userRole +
							")",
					}));
				setStaffOptionDropDown(staffDropDown);
			}
		} catch (error) {
			console.log("Error:" + error);
		}
		try {
			const response = await api.get(
				"User/select-users-that-process-settlements"
			);
			if (response?.status === 200) {
				const result = response?.data;
				const processedByDropDown = result.map((item: any) => ({
					value: item.id,
					label:
						item.firstName +
						" " +
						item.lastName +
						" ( " +
						item.userRole +
						")",
				}));
				setProcessedByOptionDropDown(processedByDropDown);
			}
		} catch (error) {
			console.log("Error:" + error);
		}
	};

	const getTableData = async () => {
		try {
			const response = await api.get("User/select-small-client-details");
			if (response.data) {
				const result = response.data;
				const sellersDropDown = result.map(
					(sellerItem: any, index: number) => ({
						value: `${sellerItem.id}-${index}`,
						label: `${sellerItem.username}  (${
							sellerItem.uniqueSellerIdentifier ?? ""
						})`,
					})
				);
				setSeller(sellersDropDown);
			}
		} catch (error: any) {
			console.log(error);
		}
	};

	useEffect(() => {
		fetchUsersAndUserRoles();
		getTableData();
	}, [currentLang]);

	return (
		<>
			<div className="mainFilterSettlement">
				<div className="firstFilter">
					<p className="settlementTitle">
						{title}{" "}
						<span className="text-[#4F16C2]">
							({settlementLength})
						</span>
					</p>
					{showRequestButton && (
						<Button
							type="primary"
							size="large"
							className="settlementButton"
							onClick={onRequestButtonClick}
						>
							<PrintIcon />
							{buttonTitle}
						</Button>
					)}
				</div>

				<div className="settlementsInputs">
					<DefaultInput
						placeholder={strings?.searchtext}
						label={strings?.searchtext}
						prefix={<CodeIcon />}
						className="inputSettlement"
						value={code}
						onChange={handleCodeChange}
						allowClear={true}
					/>
					{hasAccess("SETTLEMENTS_STAFF_IDS_ACCESS") && (
						<DefaultSelect
							placeholder={strings?.choose}
							label={strings?.responsibility}
							flexDirection="col"
							alignCenter="start"
							className="inputSettlement"
							options={staffOptionDropDown}
							value={staffIds}
							onChange={handleStaffChange}
							allowClear
						/>
					)}
					<DefaultSelect
						placeholder={strings?.choose}
						label={strings?.statuslabel}
						flexDirection="col"
						alignCenter="start"
						className="inputSettlement"
						options={settlementStatusOptions}
						value={statusIds}
						onChange={handleStatusChange}
						allowClear={true}
						onClear={() => setSelectedStatus(null)}
					/>
					{(hasAccess("SETTLEMENTS_CLIENTS_ACCESS") ||
						showReturnWarehouseManager) && (
						<DefaultSelect
							placeholder={strings?.choose}
							label={strings?.seller}
							flexDirection="col"
							alignCenter="start"
							className="inputSettlement"
							options={seller}
							value={sellerIds}
							onChange={handleSellerChange}
							allowClear
						/>
					)}
					{hasAccess("SETTLEMENTS_PROCESSED_BY_ACCESS") && (
						<DefaultSelect
							placeholder={strings?.choose}
							label={"Procesuar nga:"}
							flexDirection="col"
							alignCenter="start"
							className="inputSettlement"
							options={processedByOptionDropDown}
							value={processedByIds}
							onChange={handleProcessedByChange}
							allowClear
							onClear={() => handleProcessedByChange(null)}
						/>
					)}

					<div className="settlementsDateInputs">
						<DefaultDatePicker
							label={strings?.fromdate}
							value={fromDate}
							onChange={handleFromDateChange}
							className="settlementDatePickerInput"
						/>
						<DefaultDatePicker
							label={strings?.untill}
							value={toDate}
							onChange={handleToDateChange}
							className="settlementDatePickerInput"
						/>
					</div>
					<DefaultSelect
						placeholder={strings?.options}
						label={strings?.options}
						options={selectedOptions}
						onChange={(value: any) => {
							if (value === "export_excel") {
								onSelectedOrderStatusChange();
							} else if (value === "print_list") {
								onPrintTickets();
							} else if (value === "deliver_to_return_storage") {
								deliverToReturnStorage();
							}
						}}
						flexDirection="col"
						alignCenter="start"
						className="inputSettlement"
						allowClear
					/>
				</div>
			</div>
		</>
	);
};

export default FilterSettlementsSection;
